import React, { PropsWithChildren, useEffect, useState, createContext } from "react";
import { Button, Counter } from "monday-ui-react-core";
import Icon from "monday-ui-react-core/dist/Icon";
import CloseSmall from "monday-ui-react-core/dist/icons/CloseSmall";
import licenceCheck from "./licenceCheck";
import jwt, { VerifyOptions } from "jsonwebtoken";
import debug from "../debug";

import "./LicenceBar.css";

const log = debug("LicenceBar");

interface Props {
    licenceHost: string;
    appId: string;
    getMondaySessionToken: () => Promise<string>;
    publicKey: string;
    stripePriceId: string;
    yearlyStripePriceId: string;
}

export interface Licence {
    id: string;
    expireOn: string;
    valid: boolean;
    trial: boolean;
}

interface JWTLicenceToken {
    licence: Licence;
}

interface JWTSessionToken {
    dat: {
        account_id: string;
        client_id: string;
        user_id: string;
        slug: string;
        app_id: string;
        install_id?: string;
        is_admin: boolean;
    };
}

type ContextValue = {
    licence?: Licence;
    accountId?: string;
    licenceDaysLeft?: number;
};

export const LicenceContext = createContext({} as ContextValue);

const LicenceBar: React.FC<Props> = function ({
    licenceHost,
    appId,
    getMondaySessionToken,
    publicKey,
    stripePriceId,
    yearlyStripePriceId,
    children
}: PropsWithChildren<Props>) {
    const [licence, setLicence] = useState<Licence>();
    const [accountId, setAccountId] = useState<string>();
    const [licenceDaysLeft, setLicenceDaysLeft] = useState<number>();
    const [licenceFetchError, setLicenceFetchError] = useState<boolean>();
    const [show, setShow] = useState<boolean>(true);

    // Read cached licence
    useEffect(() => {
        if (appId && accountId) {
            const licenceId = licenceCheck.getLicenceId(appId, accountId);

            try {
                const options: VerifyOptions = {
                    algorithms: publicKey ? ["RS256"] : ["none"]
                };

                // Read licence from storage so we can quickly display the app (props.children) without waiting for licence server.
                const decoded = jwt.verify(
                    localStorage.getItem("licence") || "",
                    publicKey,
                    options
                ) as JWTLicenceToken;
                if (decoded && decoded.licence.id === licenceId) {
                    setLicence(decoded.licence);
                }
            } catch (err) {
                console.error(err);
            }
        }
    }, [publicKey, appId, accountId]);

    // Update days left
    useEffect(() => {
        function updateDaysLeft(): void {
            const nowMs = Date.now();
            const expireMs = new Date(licence!.expireOn).getTime();
            const daysLeft = (expireMs - nowMs) / (24 * 3600 * 1000);

            log("updating days left");
            setLicenceDaysLeft(daysLeft);
        }

        if (licence) {
            updateDaysLeft();
            const id = setInterval(updateDaysLeft, 3600 * 1000);

            return (): void => clearInterval(id);
        }
    }, [licence]);

    // Get licence from server
    useEffect(() => {
        async function updateLicence(): Promise<void> {
            const sessionToken = await getMondaySessionToken();
            const decoded = jwt.decode(sessionToken) as JWTSessionToken;
            const accountId = decoded.dat.account_id;
            setAccountId(accountId);

            const licence = await licenceCheck.getLicence(licenceHost, appId, sessionToken);
            let decodedLicence;

            try {
                const options: VerifyOptions = {
                    algorithms: publicKey ? ["RS256"] : ["none"]
                };

                log({ options, publicKey, licence });
                decodedLicence = jwt.verify(licence, publicKey, options) as JWTLicenceToken;
            } catch (err) {
                console.error(err);
            }

            if (decodedLicence) {
                setLicence(decodedLicence.licence);
                localStorage.setItem("licence", licence);
                setLicenceFetchError(false);
            } else {
                setLicenceFetchError(true);
            }
        }

        updateLicence();
    }, [appId, getMondaySessionToken, publicKey, licenceHost]);

    if ((!licence || !licence.valid) && licenceFetchError) {
        return <div>Unable to contact licence server, check your internet connection.</div>;
    }

    // Waiting for licence
    if (!licence || !accountId) {
        return <div />;
    }

    const handleSubscribeNow = (priceId: string) => (e: React.MouseEvent): void => {
        e.stopPropagation();
        e.preventDefault();
        const a = document.createElement("a");
        a.href = licenceCheck.getPaymentUrl(licenceHost, appId, accountId, priceId);
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
    };

    const hideLicenceBar = (e: React.MouseEvent): void => {
        setShow(false);
        e.stopPropagation();
    };

    return (
        <div className="licence">
            {(!licence.valid || (licence.trial && show)) && (
                <div className="licence_bar" onClick={handleSubscribeNow(stripePriceId)}>
                    <span className="licence_bar__text">
                        {licence.trial && (
                            <>
                                Trial version
                                {licenceDaysLeft !== undefined && (
                                    <>
                                        ,&nbsp;
                                        <Counter
                                            count={Math.ceil(licenceDaysLeft)}
                                            kind={Counter.kinds.FILL}
                                            color={
                                                licenceDaysLeft < 7 ? Counter.colors.NEGATIVE : Counter.colors.POSITIVE
                                            }
                                        />
                                        &nbsp;day{licenceDaysLeft > 1 ? "s" : ""} left.
                                    </>
                                )}
                            </>
                        )}
                        {!licence.valid && <>Licence expired.</>}
                    </span>
                    <span className="licence_bar__buttons">
                        Subscribe
                        <Button
                            kind={Button.kinds.PRIMARY}
                            color={Button.colors.POSITIVE}
                            size={Button.sizes.SMALL}
                            noSidePadding
                            className="licence_bar__button first_button"
                        >
                            Monthly ($19/month)
                        </Button>
                        or
                        <Button
                            kind={Button.kinds.PRIMARY}
                            color={Button.colors.POSITIVE}
                            size={Button.sizes.SMALL}
                            className="licence_bar__button"
                            onClick={handleSubscribeNow(yearlyStripePriceId)}
                        >
                            Yearly ($209/year)
                        </Button>
                    </span>
                    <div className="licence_bar__close" onClick={hideLicenceBar}>
                        {licence.trial && (
                            <Icon
                                iconType={Icon.type.SVG}
                                icon={CloseSmall}
                                iconLabel="close"
                                clickable
                                iconSize={20}
                            />
                        )}
                    </div>
                </div>
            )}

            <div className="licence_content">
                {licence.valid ? (
                    <LicenceContext.Provider value={{ licence, accountId, licenceDaysLeft }}>
                        {children}
                    </LicenceContext.Provider>
                ) : (
                    <div className="no_licence">
                        <p>
                            Sorry, we couldn&apos;t find an active licence (licence id: {licence.id}). Please{" "}
                            <span className="no_licence__button" onClick={handleSubscribeNow(stripePriceId)}>
                                Subscribe
                            </span>{" "}
                            to continue using Funnels for Monday.com.
                        </p>
                        <p>(Once subscribed, please refresh this screen.)</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LicenceBar;
